import API from '@sparefoot/react-core/symbols/api';
import { normalize } from 'normalizr';
import RequestError from 'utils/error/request';
import fetchClient from 'utils/fetchClient';
import { omitBy, isUndefined } from 'lodash';
import { getCookie } from 'utils/localStorage/localStorage';
import { getTraceId } from 'store/modules/app';

export function callApi(url, schema, options = {}) {
	const { method = 'GET', data, ...rest } = options;
	const methodKey = method.toLowerCase();

	const request =
		methodKey === 'get' || methodKey === 'delete'
			? fetchClient[methodKey](url, rest)
			: fetchClient[methodKey](url, data, rest);

	return request
		.then(({ data: responseData }) =>
			schema
				? {
						...normalize(responseData, schema),
						meta: responseData.meta || {},
					}
				: responseData,
		)
		.catch((error) => {
			throw new RequestError(error);
		});
}

const apiMiddleware = (store) => (next) => (action) => {
	const callAPI = action[API];

	// Skip if not an API Call
	if (callAPI === undefined) {
		return next(action);
	}

	const actionWith = (actionData) => {
		const { [API]: _, ...rest } = { ...action, ...actionData };
		return rest;
	};

	const { app, user } = store.getState();
	const {
		types: [
			// Action Types
			requestType,
			successType,
			failureType,
		],
		endpoint, // API Endpoint
		method, // REST Method
		data, // Post Data
		query, // Query Params
		meta, // Action Meta
		schema, // Entity Schema
	} = callAPI;

	// Fire Request Action
	next(
		actionWith(
			omitBy({ type: requestType, meta, query, data }, isUndefined),
		),
	);

	const ssagHost = app.ssag_host_internal || app.ssag_host;
	const requestURL = `${ssagHost}${endpoint}`;

	// Build Request Options
	const options = {
		headers: {
			token: process.env.SSAG_TOKEN,
			'app-id': app.id,
			'site-id': user.siteId,
			'visit-id': user.visitId,
			'traffic-source-id': user.traffic_source_id,
			cid: user.cid,
			ctid: user.ctid,
			'x-is-bot':
				user.visitId === 'bot' || user.visitId === 'error' ? 1 : 0,
			'x-sparefoot-ip': user.user_ip,
			// Not 'user-agent': that is a forbidden header name, so fetch() drops it.
			// Omitted entirely when unknown, or fetch would send the string "null".
			...(user.userAgent ? { 'client-user-agent': user.userAgent } : {}),
			'x-geo-city': user.geolocation.city,
			'x-geo-country': user.geolocation.country,
			'x-geo-region': user.geolocation.region,
			'x-geo-latitude': user.geolocation.latitude,
			'x-geo-longitude': user.geolocation.longitude,
			// Tells the gateway these values are our placeholder, not a
			// detected location, so it doesn't report them as real geo.
			...(user.geolocation.isFallback
				? { 'x-geo-is-fallback': '1' }
				: {}),
			'x-trace-id': getTraceId(store.getState()),
		},
		method: method || 'GET',
		data,
		query,
	};

	const isCreateBooking =
		(method || 'GET') === 'POST' && endpoint === '/api/booking';

	if (isCreateBooking) {
		options.headers.Authorization = '';
	} else if (process.env.RENDER_ENV === 'client') {
		const clientJwt = getCookie('jwt');
		options.headers.Authorization = clientJwt ? `Bearer ${clientJwt}` : '';
	} else if (user.cookies?.jwt) {
		options.headers.Authorization = `Bearer ${user.cookies.jwt}`;
	}

	const traceId = getTraceId(store.getState());

	return callApi(requestURL, schema, options)
		.then((payload) =>
			next(
				actionWith({
					type: successType,
					meta,
					payload,
				}),
			),
		)
		.catch(({ error, status, errorMessage, response }) =>
			next(
				actionWith({
					type: failureType,
					meta,
					traceId,
					status: status,
					error:
						errorMessage ||
						error?.message ||
						'Something really bad happened',
					payload: response,
				}),
			),
		);
};

export default apiMiddleware;
